/*
 * 密码门禁（自托管多账号开关）。
 *
 * 打开页面时先显示一个「站点维护中」的伪装首页（无任何登录特征，
 * 用于规避钓鱼网站误报）；在标题上快速连点 5 次，才露出密码框。
 * 输入不同的密码，进入对应的已登录账号。
 *
 * 密码只在浏览器 <-> /api/auth（nginx -> 本地 Node 校验服务）之间发送，
 * 服务端只存 scrypt 哈希，明文密码不落盘。
 *
 * 解锁状态不持久化：每次加载都重新走门禁（伪装页 -> 5 连点 -> 密码）。
 * 仅账号切换时写一个一次性标记，重载后读一次即删，避免切换账号被二次拦截。
 * 校验通过后按返回的账号号跳转到 ?account=N（账号 1 无参数）。
 */
import {getCurrentAccount} from '@lib/accounts/getCurrentAccount';
import {changeAccount} from '@lib/accounts/changeAccount';
import {syncSessionForAccount, installLogoutSessionCleanup} from '@lib/sessionSync';
import type {ActiveAccountNumber} from '@lib/accounts/types';

const UNLOCK_KEY = 'tweb_pw_gate_account';

export async function ensurePasswordGate() {
  installLogoutSessionCleanup();

  const current = getCurrentAccount();

  let unlocked: string | null = null;
  try {
    unlocked = sessionStorage.getItem(UNLOCK_KEY);
    sessionStorage.removeItem(UNLOCK_KEY); // 一次性标记：读后即删
  } catch(e) { /* ignore */ }

  if(unlocked === String(current)) {
    return; // 刚由账号切换整页重载而来，本标签页已解锁当前账号
  }

  // 伪装首页：先显示「站点维护中」，连点标题 5 次才露出密码框
  await showMaintenanceLanding();

  const {account, password, session} = await showPasswordOverlay();

  // 会话同步：本机已登录则加密上传，未登录则从服务器下载解密导入
  await syncSessionForAccount(account, password, session);

  // 输入密码对应的账号与当前 URL 账号不一致 -> 写一次性解锁标记并跳转（整页重载，本执行流中断）。
  // 同账号则什么都不写，直接放行；下次刷新 / 新访问仍会重新走门禁。
  if(account !== getCurrentAccount()) {
    try {
      sessionStorage.setItem(UNLOCK_KEY, String(account));
    } catch(e) { /* ignore */ }
    changeAccount(account, false, true);
    await new Promise<void>(() => {});
  }
}

const SECRET_CLICKS = 5;

// 「站点维护中」伪装首页：无登录特征，连点标题 SECRET_CLICKS 次后进入密码框。
function showMaintenanceLanding(): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#fff;color:#333;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;text-align:center;';

    const title = document.createElement('div');
    title.textContent = '站点维护中';
    title.style.cssText = 'font-size:22px;font-weight:600;margin-bottom:8px;cursor:pointer;user-select:none;';
    overlay.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.textContent = '请稍后再来';
    subtitle.style.cssText = 'font-size:14px;color:#999;';
    overlay.appendChild(subtitle);

    document.body.appendChild(overlay);

    let count = 0;
    let last = 0;
    const onClick = () => {
      const now = Date.now();
      if(now - last > 2000) count = 0; // 间隔超过 2 秒重新计数
      last = now;
      if(++count >= SECRET_CLICKS) {
        title.removeEventListener('click', onClick);
        overlay.remove();
        resolve();
      }
    };
    title.addEventListener('click', onClick);
  });
}

function showPasswordOverlay(): Promise<{account: ActiveAccountNumber, password: string, session: {salt: string, iv: string, ciphertext: string} | null}> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.62);';

    const card = document.createElement('div');
    card.style.cssText = 'width:280px;max-width:calc(100vw - 32px);background:#fff;color:#111;border-radius:14px;padding:24px 20px;box-shadow:0 8px 40px rgba(0,0,0,.4);font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;box-sizing:border-box;';
    overlay.appendChild(card);

    const title = document.createElement('div');
    title.textContent = '请输入访问密码';
    title.style.cssText = 'font-size:17px;font-weight:600;text-align:center;margin-bottom:16px;';
    card.appendChild(title);

    const input = document.createElement('input');
    input.type = 'password';
    input.autocomplete = 'current-password';
    input.placeholder = '密码';
    input.style.cssText = 'width:100%;box-sizing:border-box;padding:10px 12px;font-size:15px;border:1px solid #c9c9c9;border-radius:8px;outline:none;margin-bottom:12px;';
    card.appendChild(input);

    const error = document.createElement('div');
    error.style.cssText = 'display:none;color:#c0392b;font-size:13px;text-align:center;margin-bottom:10px;';
    card.appendChild(error);

    const button = document.createElement('button');
    button.textContent = '进入';
    button.style.cssText = 'width:100%;box-sizing:border-box;padding:10px;font-size:15px;font-weight:600;color:#fff;background:#2481cc;border:none;border-radius:8px;cursor:pointer;';
    card.appendChild(button);

    document.body.appendChild(overlay);
    input.focus();

    let submitting = false;

    const showError = (msg: string) => {
      error.textContent = msg;
      error.style.display = 'block';
      input.focus();
      input.select();
    };

    const submit = async() => {
      if(submitting) return;
      const password = input.value;
      if(!password) {
        showError('请输入密码');
        return;
      }

      submitting = true;
      button.disabled = true;
      button.textContent = '验证中…';

      try {
        const resp = await fetch('/api/auth', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({password})
        });

        if(resp.ok) {
          const data = await resp.json();
          overlay.remove();
          resolve({account: data.account as ActiveAccountNumber, password, session: data.session || null});
          return;
        }

        if(resp.status === 429) {
          showError('尝试次数过多，请稍后再试');
        } else {
          showError('密码错误，请重试');
        }
      } catch(e) {
        showError('网络错误，请重试');
      }

      submitting = false;
      button.disabled = false;
      button.textContent = '进入';
      input.value = '';
    };

    button.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      if(e.key === 'Enter') submit();
    });
  });
}
